import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import UserDropdown from './UserDropdown';
import { PRODUCT_NAME } from '../config/brand';

const tabKeys = [
    { path: '/', key: 'nav.insights' },
    { path: '/feed', key: 'nav.feed' },
    { path: '/workbench', key: 'nav.workbench' },
    { path: '/ai-future', key: 'nav.aiFuture' },
    { path: '/about', key: 'nav.about' },
];

const TabNavigation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { language, setLanguage, t } = useLanguage();
    const currentDate = new Date();
    const [viewportWidth, setViewportWidth] = useState(() => (
        typeof window === 'undefined' ? 1024 : window.innerWidth
    ));
    const isMobile = viewportWidth <= 720;

    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const styles = {
        nav: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: isMobile ? '0.55rem 0.75rem' : 0,
            padding: isMobile ? '0.65rem 1rem 0.75rem' : '0 2rem',
            height: isMobile ? 'auto' : '56px',
            backgroundColor: 'rgba(255, 255, 255, 0.72)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
        },
        brand: {
            fontSize: isMobile ? '1rem' : '1.1rem',
            fontWeight: '700',
            letterSpacing: '-0.02em',
            color: '#1D1D1F',
            flex: isMobile ? '1 1 auto' : '0 0 auto',
            lineHeight: '1.05',
        },
        tabGroup: {
            display: 'flex',
            gap: '4px',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            borderRadius: '10px',
            padding: '3px',
            order: isMobile ? 3 : 0,
            width: isMobile ? '100%' : 'auto',
            overflowX: isMobile ? 'auto' : 'visible',
            WebkitOverflowScrolling: isMobile ? 'touch' : 'auto',
            scrollbarWidth: isMobile ? 'none' : 'auto',
        },
        tab: {
            padding: isMobile ? '7px 13px' : '6px 18px',
            borderRadius: '8px',
            border: 'none',
            fontSize: isMobile ? '0.82rem' : '0.85rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            flex: isMobile ? '0 0 auto' : '0 1 auto',
            whiteSpace: 'nowrap',
        },
        tabActive: {
            backgroundColor: '#fff',
            color: '#1D1D1F',
            fontWeight: '600',
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
        },
        tabInactive: {
            backgroundColor: 'transparent',
            color: '#86868B',
        },
        rightGroup: {
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '8px' : '12px',
            marginLeft: isMobile ? 'auto' : 0,
        },
        date: {
            fontSize: '0.85rem',
            color: '#86868B',
            fontWeight: '500',
            display: isMobile ? 'none' : 'block',
        },
        langToggle: {
            padding: '4px 10px',
            borderRadius: '8px',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            fontSize: '0.78rem',
            fontWeight: '600',
            cursor: 'pointer',
            color: '#1D1D1F',
            transition: 'all 0.15s ease',
        },
    };

    const dateLocale = language === 'zh' ? 'zh-CN' : 'en-US';

    return (
        <nav style={styles.nav} role="navigation" aria-label="Main navigation">
            <div style={styles.brand}>{PRODUCT_NAME}</div>
            <div style={styles.tabGroup} role="tablist" aria-label="Page navigation">
                {tabKeys.map((tab) => {
                    const isActive = tab.path === '/'
                        ? (location.pathname === '/' || location.pathname.startsWith('/article'))
                        : location.pathname.startsWith(tab.path);
                    return (
                        <button
                            key={tab.path}
                            onClick={() => navigate(tab.path)}
                            style={{
                                ...styles.tab,
                                ...(isActive ? styles.tabActive : styles.tabInactive),
                            }}
                            role="tab"
                            aria-selected={isActive}
                            aria-label={`Navigate to ${t(tab.key)}`}
                        >
                            {t(tab.key)}
                        </button>
                    );
                })}
            </div>
            <div style={styles.rightGroup}>
                <div style={styles.date} aria-label={`Current date: ${currentDate.toLocaleDateString(dateLocale)}`}>
                    {currentDate.toLocaleDateString(dateLocale, {
                        month: 'short',
                        day: 'numeric',
                        weekday: 'short',
                    })}
                </div>
                <button
                    onClick={() => setLanguage(language === 'zh' ? 'en' : 'zh')}
                    style={styles.langToggle}
                    aria-label={language === 'zh' ? 'Switch to English' : '切换到中文'}
                >
                    {language === 'zh' ? 'EN' : '中'}
                </button>
                <UserDropdown />
            </div>
        </nav>
    );
};

export default TabNavigation;
